// DateRangePicker.jsx — v119.144 Professional per-page date range picker.
// Presets (اليوم، أمس، هذا الأسبوع، الأسبوع الماضي، هذا الشهر، الشهر الماضي،
// آخر 7 أيام، آخر 30 يوماً، السنة، مخصص) with Arabic labels. Emits KSA-aligned
// YYYY-MM-DD strings via onChange({from, to, grain, preset}).
//
// Business-day aware: the "day" boundary is 07:00→06:59 KSA — but the picker
// simply returns calendar dates (YYYY-MM-DD); the endpoint interprets them via
// `business_day_bounds_for_date`.
import React, { useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { WEEK_START_DEFAULT, ksaAddDays, ksaEndOfMonth, ksaEndOfQuarter, ksaEndOfHalf, ksaStartOfMonth, ksaStartOfQuarter, ksaStartOfHalf, ksaStartOfWeek, ksaStartOfYear, ksaToday } from "./lib/ksaTime";

// v119.190 — KSA Business-Day aware date helpers.
// Business day runs 07:00 KSA → 06:59 KSA next day. Before 07:00 KSA we're
// still in YESTERDAY's business day. Uses UTC+3 (KSA offset) so it works
// regardless of the client's device timezone.
const _ksaBusinessDay = (offsetDays = 0) => {
  // Convert current UTC → KSA time by adding 3 hours
  const utcMs = Date.now();
  const ksaMs = utcMs + 3 * 3600 * 1000;
  // Subtract 7 hours to shift start-of-business-day (07:00) to 00:00
  const shifted = new Date(ksaMs - 7 * 3600 * 1000);
  // Now the date part of `shifted` (interpreted as UTC) IS the business day.
  const y = shifted.getUTCFullYear();
  const m = shifted.getUTCMonth();
  const d = shifted.getUTCDate() + offsetDays;
  const target = new Date(Date.UTC(y, m, d));
  return `${target.getUTCFullYear()}-${String(target.getUTCMonth()+1).padStart(2,"0")}-${String(target.getUTCDate()).padStart(2,"0")}`;
};

// ⑤/L3-أ — كل حساب تقويمي عبر lib/ksaTime.js بتوقيت السعودية (كان
// _startOfWeek يحسب بـgetDay على توقيت الجهاز ثم يكتب بتوقيت KSA ⇒ بين
// 00:00 و03:00 KSA يبدأ «هذا الأسبوع» الاثنين). بداية الأسبوع إعداد.
export const PRESETS = [
  { id: "today",       label: "اليوم" },
  { id: "yesterday",   label: "أمس" },
  { id: "this_week",   label: "هذا الأسبوع" },
  { id: "last_week",   label: "الأسبوع الماضي" },
  { id: "this_month",  label: "هذا الشهر" },
  { id: "last_month",  label: "الشهر الماضي" },
  { id: "last7",       label: "آخر 7 أيام" },
  { id: "last30",      label: "آخر 30 يوم" },
  { id: "last90",      label: "آخر 90 يوم" },
  { id: "this_quarter", label: "هذا الربع" },
  { id: "this_half",    label: "هذا النصف" },
  { id: "this_year",   label: "هذه السنة" },
  { id: "custom",      label: "مخصّص" },
];

// 2026-10-11 (جولة إصلاح — البند 3): "this_week"/"this_month" كانا يرجعان
// {from: بداية الفترة, to: اليوم} ("period-to-date") — في أول يوم بالفترة
// (مثلاً الأحد نفسه، أول يوم الأسبوع) يتساوى from مع to فيظهر الفلتر
// كـ"يوم واحد". الإصلاح: «أسبوعي»/«شهري» = الفترة التقويمية الحالية
// **كاملة** (بدايتها → نهايتها)، لا "حتى اليوم فقط" — يطابق معنى
// «الأسبوع الحالي»/«الشهر الحالي» حرفياً، ويُزيل حالة الانهيار ليوم واحد
// جذرياً (لا رقعة لحالة الأحد فقط). weekStartIdx يُمرَّر من الإعدادات
// (week_start_day عبر /command-room/settings/tenant) — Sunday=0 افتراضياً.
// 2026-10-25 — عطل: "اليوم" كان الوحيد المعتمد على حدّ يوم العمل
// (_ksaBusinessDay، قَطعه 07:00 KSA) بينما "هذا الأسبوع/الشهر/آخر 7..."
// تعتمد تاريخاً ميلادياً خاماً (ksaToday) — فبين 00:00-07:00 KSA (يوم
// العمل الجاري لم ينتهِ فعلياً) قد يقع "اليوم" *خارج* نافذة "هذا
// الأسبوع" المحسوبة من التاريخ الميلادي التالي (مثال: الأحد 00:00-07:00
// يُحسب يوم عمل السبت الجاري "اليوم"، بينما "هذا الأسبوع" يبدأ بالفعل من
// الأحد التالي) — ينكسر معه يوم⊆أسبوع⊆شهر. الإصلاح: كل الفترات هنا تُبنى
// من نفس الأساس الواحد (يوم العمل الجاري)، فتتطابق جميعاً بعد 07:00 KSA
// تماماً كما كانت، وتبقى متّسقة فيما بينها قبل 07:00 أيضاً.
export const resolvePreset = (id, weekStartIdx = WEEK_START_DEFAULT) => {
  const t = _ksaBusinessDay(0);
  switch (id) {
    // Business-day aware for today/yesterday (07:00 → 06:59 KSA)
    case "today":       return { from: _ksaBusinessDay(0),  to: _ksaBusinessDay(0) };
    case "yesterday":   return { from: _ksaBusinessDay(-1), to: _ksaBusinessDay(-1) };
    case "this_week":   { const ws = ksaStartOfWeek(t, weekStartIdx);
                          return { from: ws, to: ksaAddDays(ws, 6) }; }
    case "last_week":   { const ws = ksaStartOfWeek(ksaAddDays(t, -7), weekStartIdx);
                          return { from: ws, to: ksaAddDays(ws, 6) }; }
    case "this_month":  return { from: ksaStartOfMonth(t), to: ksaEndOfMonth(t) };
    case "last_month":  { const s = ksaStartOfMonth(ksaAddDays(ksaStartOfMonth(t), -1));
                          return { from: s, to: ksaEndOfMonth(s) }; }
    case "last7":       return { from: ksaAddDays(t, -6),  to: t };
    case "last30":      return { from: ksaAddDays(t, -29), to: t };
    case "last90":      return { from: ksaAddDays(t, -89), to: t };
    case "this_quarter": return { from: ksaStartOfQuarter(t), to: ksaEndOfQuarter(t) };
    case "this_half":    return { from: ksaStartOfHalf(t),    to: ksaEndOfHalf(t) };
    case "this_year":   return { from: ksaStartOfYear(t),  to: t };
    default:            return null;
  }
};

// L2 (2026-02) — عطل إنتاج 24-10: ضغط preset («هذا الشهر»/«الشهر الماضي»/
// إلخ) في القائمة كان يُحدِّث dateRange وحده دون grain، فتُرسَل الصفحة
// بدقّة (grain) قديمة لا تطابق الفترة المختارة فعلياً («هذا الشهر» أُرسل
// بـgrain=day وdate=31-10 ⇐ يوم مستقبلي واحد = صفر). المرساة الآن مصدر
// واحد: كل preset له grain محدَّد ضمنياً يُرفَق في onChange نفسه، فيتحدّث
// مع dateRange في نفس اللحظة لا في دورتين منفصلتين.
//
// N1 (2026-02) — عطل من نفس فئة L: «آخر 7/30/90 يوماً» لم يكن لها قيمة في
// هذه الخريطة، فيبقى grain متروكاً من العرض السابق (من «يومي» يُحسب يوم
// واحد بدل 30). لا قيمة متروكة بعد الآن: كل preset إما grain تقويمي صريح
// أو "range" (يُرسَل عبر from/to — مسار الفترة المخصّصة نفسه، لا grain
// إطلاقاً)، فتُفحص الخريطة بالكامل ولا حالة محايدة تسقط بينهما.
export const PRESET_GRAIN = {
  today: "day", yesterday: "day",
  this_week: "week", last_week: "week",
  this_month: "month", last_month: "month",
  this_quarter: "quarter", this_half: "half",
  this_year: "year",
  last7: "range", last30: "range", last90: "range",
};

// AD2 — إزاحة الفترة (0=الحالية، 1=السابقة) لنفس grain — يستهلكها أي
// مستدعٍ يبني فترة حيّة من period_engine (مصدر واحد مع التقرير).
export const PRESET_OFFSET = { yesterday: 1, last_week: 1, last_month: 1 };

// Compact chip-style picker suitable for placement in page headers.
export default function DateRangePicker({
  value,             // { from, to, preset }
  onChange,          // ({from,to,preset}) => void
  presets = PRESETS,
  className = "",
  testId = "date-range-picker",
}) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const [anchor, setAnchor] = useState({ top: 0, right: 0, maxHeight: undefined, bottom: undefined });
  const openMenu = () => {
    const r = toggleRef.current?.getBoundingClientRect();
    if (r) {
      const menuW = Math.min(380, window.innerWidth - 24);
      const right = Math.min(
        Math.max(12, window.innerWidth - r.right),
        window.innerWidth - menuW - 12,
      );
      // M7-hardening (2026-02) — عطل اكتشفه testing_agent: القائمة على سطح
      // المكتب بلا max-height ولا انقلاب لأعلى، فتمتد خارج أسفل الشاشة إن
      // كان الزر قريباً من أسفل الصفحة (presets مثل this_quarter/this_half/
      // this_year/custom تصبح بلا وصول). الإصلاح: تُحسَب المساحة المتاحة
      // أسفل الزر وأعلاه؛ إن كانت أسفله أضيق من أعلاه تنقلب القائمة لأعلى،
      // ودوماً بارتفاع أقصى + تمرير داخلي بدل الامتداد خارج حدود الشاشة.
      const spaceBelow = window.innerHeight - r.bottom - 20;
      const spaceAbove = r.top - 20;
      const showAbove = spaceBelow < 320 && spaceAbove > spaceBelow;
      setAnchor({
        top: showAbove ? undefined : r.bottom + 8,
        bottom: showAbove ? window.innerHeight - r.top + 8 : undefined,
        right,
        maxHeight: Math.max(200, showAbove ? spaceAbove : spaceBelow),
      });
    }
    setOpen((v) => !v);
  };
  const [viewMonth, setViewMonth] = useState(() => ksaStartOfMonth(value?.from || ksaToday()));
  const [selecting, setSelecting] = useState(null); // {from: 'YYYY-MM-DD'} while picking to

  const current = useMemo(() => value || { from: "", to: "", preset: "last30" }, [value]);
  const currentPreset = presets.find((p) => p.id === current.preset)
    || presets.find((p) => p.id === "custom");

  const summary = useMemo(() => {
    if (currentPreset && currentPreset.id !== "custom") return currentPreset.label;
    if (current.from && current.to) {
      if (current.from === current.to) return current.from;
      return `${current.from} → ${current.to}`;
    }
    return "اختر الفترة";
  }, [currentPreset, current]);
  // M2 (2026-02) — نص عربي (تسمية جاهزة) يبقى RTL طبيعياً؛ نص تاريخ خام
  // (مفرد أو نطاق مع سهم) يحتاج عزل dir="ltr" صريح (راجع التعليق عند الاستخدام أدناه).
  const summaryIsRawDate = !(currentPreset && currentPreset.id !== "custom") && current.from;

  const apply = (id) => {
    if (id === "custom") return;
    const r = resolvePreset(id);
    const g = PRESET_GRAIN[id];
    // N1 — "range": لا grain يُرسَل إطلاقاً (بدل تركه من حالة سابقة)؛
    // isRange=true يوجِّه الصفحة لمسار from/to نفسه المستخدم للفترة المخصّصة.
    if (r) { onChange({ ...r, preset: id, grain: g === "range" ? undefined : g, isRange: g === "range" }); setOpen(false); }
  };

  // Calendar grid — 6 weeks × 7 days for current viewMonth
  const monthDays = useMemo(() => {
    const start = ksaStartOfWeek(viewMonth);  // الأحد (WEEK_START_DEFAULT) — يطابق رأس WEEK_AR
    return Array.from({ length: 42 }, (_, i) => ksaAddDays(start, i));
  }, [viewMonth]);
  const MONTH_NAMES_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
                          "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
  const WEEK_AR = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];

  const clickDay = (d) => {
    const iso = d;
    if (!selecting) {
      setSelecting({ from: iso });
      return;
    }
    // completing the range
    const from = selecting.from < iso ? selecting.from : iso;
    const to = selecting.from < iso ? iso : selecting.from;
    onChange({ from, to, preset: "custom", isRange: true });
    setSelecting(null);
    setOpen(false);
  };

  const isInRange = (d) => {
    const iso = d;
    if (selecting) return iso === selecting.from;
    if (!current.from || !current.to) return false;
    return iso >= current.from && iso <= current.to;
  };
  const isStart = (d) => d === (selecting?.from || current.from);
  const isEnd = (d) => !selecting && d === current.to;

  return (
    <div className={`relative inline-block ${className}`} dir="rtl" data-testid={testId}>
      <button type="button"
              ref={toggleRef}
              onClick={openMenu}
              data-testid={`${testId}-toggle`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "#FFFFFF",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 13, fontWeight: 600, color: "#111827",
                cursor: "pointer",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2A85FF"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; }}>
        <span style={{ opacity: 0.5 }}>📅</span>
        {/* M2 (2026-02) — عطل: نطاق التاريخ المخصّص (رقمين إنجليزي + سهم) كان
            بلا dir="ltr" صريح هنا (خلافاً للقاعدة الداخلية بالفعل في لوحة
            التقويم أدناه سطر ~348)، فيعتمد على استدلال bidi الضمني داخل
            سياق RTL — يظهر أحياناً باتجاه معكوس. الإصلاح: نفس عزل dir="ltr"
            المُطبَّق أصلاً هناك، على زر التشغيل الظاهر دوماً أيضاً — لنص
            التاريخ الخام فقط (التسمية العربية الجاهزة تبقى RTL طبيعياً). */}
        {summaryIsRawDate
          ? <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{summary}</span>
          : <span>{summary}</span>}
        <span style={{ opacity: 0.4, fontSize: 10 }}>▼</span>
      </button>

      {open && createPortal(
        <div dir="rtl">
          <div className="drp-backdrop"
               data-testid={`${testId}-backdrop`}
               onClick={() => { setOpen(false); setSelecting(null); }}
               onTouchEnd={(e) => { e.preventDefault(); setOpen(false); setSelecting(null); }} />
          <div data-testid={`${testId}-menu`}
               className="drp-menu"
               onTouchEnd={(e) => e.stopPropagation()}
               style={{
                 top: anchor.top,
                 bottom: anchor.bottom,
                 right: anchor.right,
                 maxHeight: anchor.maxHeight,
                 overflowY: "auto",
                 background: "#FFFFFF",
                 borderRadius: 14,
                 border: "1px solid var(--border)",
                 boxShadow: "0 20px 40px rgba(17,24,39,0.15), 0 0 0 1px rgba(17,24,39,0.06)",
                 padding: 14,
                 display: "flex",
                 flexWrap: "wrap",
                 gap: 12,
                 touchAction: "manipulation",
                 WebkitTapHighlightColor: "transparent",
               }}>
            {/* Left: Presets column */}
            <div style={{ width: 120, borderInlineEnd: "1px solid var(--border)", paddingInlineEnd: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", marginBottom: 6 }}>
                اختصارات
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {presets.filter((p) => p.id !== "custom").map((p) => {
                  const active = current.preset === p.id;
                  return (
                    <button key={p.id}
                            onClick={() => apply(p.id)}
                            data-testid={`${testId}-preset-${p.id}`}
                            style={{
                              textAlign: "right",
                              padding: "6px 10px",
                              borderRadius: 6,
                              fontSize: 12, fontWeight: active ? 700 : 500,
                              border: "none",
                              background: active ? "#EFF6FF" : "transparent",
                              color: active ? "#2A85FF" : "#374151",
                              cursor: "pointer",
                              transition: "background 0.15s ease",
                            }}
                            onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#F9FAFB"; }}
                            onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Calendar */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Header: month nav */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <button type="button"
                        onClick={() => setViewMonth(ksaStartOfMonth(ksaAddDays(viewMonth, -1)))}
                        style={{ background: "transparent", border: "none", cursor: "pointer",
                                 padding: 4, borderRadius: 4, color: "#6B7280", fontSize: 14 }}>
                  ‹
                </button>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                  {MONTH_NAMES_AR[Number(viewMonth.slice(5, 7)) - 1]} {viewMonth.slice(0, 4)}
                </div>
                <button type="button"
                        onClick={() => setViewMonth(ksaAddDays(ksaEndOfMonth(viewMonth), 1))}
                        style={{ background: "transparent", border: "none", cursor: "pointer",
                                 padding: 4, borderRadius: 4, color: "#6B7280", fontSize: 14 }}>
                  ›
                </button>
              </div>
              {/* Weekday header */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
                {WEEK_AR.map((w) => (
                  <div key={w} style={{ fontSize: 9, fontWeight: 600, color: "#9CA3AF", textAlign: "center", padding: 4 }}>
                    {w}
                  </div>
                ))}
              </div>
              {/* Days grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                {monthDays.map((d) => {
                  const inMonth = d.slice(0, 7) === viewMonth.slice(0, 7);
                  const inRange = isInRange(d);
                  const isS = isStart(d);
                  const isE = isEnd(d);
                  const today = d === ksaToday();
                  return (
                    <button key={d}
                            onClick={() => clickDay(d)}
                            type="button"
                            className="drp-day"
                            data-testid={`${testId}-day-${d}`}
                            style={{
                              padding: "6px 0", borderRadius: 6, border: "none",
                              touchAction: "manipulation",
                              WebkitTapHighlightColor: "transparent",
                              fontSize: 12,
                              fontWeight: (isS || isE) ? 800 : (today ? 700 : 500),
                              background: (isS || isE) ? "#2A85FF"
                                : inRange ? "#EFF6FF"
                                : "transparent",
                              color: (isS || isE) ? "#FFFFFF"
                                : today ? "#2A85FF"
                                : inMonth ? "#111827" : "#D1D5DB",
                              cursor: "pointer",
                              transition: "background 0.1s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (!isS && !isE && !inRange) e.currentTarget.style.background = "#F9FAFB";
                            }}
                            onMouseLeave={(e) => {
                              if (!isS && !isE && !inRange) e.currentTarget.style.background = "transparent";
                            }}>
                      {Number(d.slice(8, 10))}
                    </button>
                  );
                })}
              </div>
              {/* Footer: hint + apply */}
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)",
                             display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 10, color: "#6B7280", flex: 1 }}>
                  {selecting ? (
                    <>البداية: <span dir="ltr" style={{ fontWeight: 700, color: "#2A85FF" }}>{selecting.from}</span> — اختر تاريخ النهاية</>
                  ) : current.from && current.to ? (
                    <span dir="ltr" style={{ direction: "ltr" }}>{current.from} → {current.to}</span>
                  ) : "اضغط يوم للبدء"}
                </div>
                <button onClick={() => { setSelecting(null); setOpen(false); }}
                        data-testid={`${testId}-apply`}
                        style={{
                          background: "#2A85FF", color: "#FFFFFF",
                          border: "none", borderRadius: 6,
                          padding: "6px 14px", fontSize: 12, fontWeight: 700,
                          cursor: "pointer",
                        }}>
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
